import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, Droplets, Activity, AlertTriangle, TrendingUp, CheckCircle, MoreVertical, MessageSquare, Mail, Phone, Bell, Zap, Shield, Wifi, FileText } from 'lucide-react';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';

const Dashboard = ({ setActiveModule }) => {
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actualSchemesCount, setActualSchemesCount] = useState(0);

  // Generate stable 7-day trend data with real dates and actual schemes count
  const generateTrendsData = (schemesCount) => {
    const trendsData = [];
    const today = new Date();
    
    // Use date string as seed for consistent random values per day
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toDateString();
      
      // Generate stable random data based on date (won't change for same date)
      // Simple hash function to get consistent "random" value for each date
      let hash = 0;
      for (let j = 0; j < dateStr.length; j++) {
        hash = ((hash << 5) - hash) + dateStr.charCodeAt(j);
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      // Use hash to generate consistent values for this specific date
      const incidents = 20 + (Math.abs(hash) % 40); // 20-60 incidents
      const resolvedRate = 0.6 + ((Math.abs(hash >> 8) % 30) / 100); // 60-90%
      const resolved = Math.floor(incidents * resolvedRate);
      
      trendsData.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date.toISOString(),
        schemes: schemesCount, // Use actual schemes count
        incidents: incidents,
        resolved: resolved
      });
    }
    
    return trendsData;
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Check for day change and regenerate data if needed
    const checkDayChange = setInterval(() => {
      const currentDate = new Date().toDateString();
      const lastTrendDate = trends.length > 0 ? new Date(trends[trends.length - 1].fullDate).toDateString() : null;
      
      // Only regenerate if we've moved to a new day
      if (lastTrendDate && currentDate !== lastTrendDate && actualSchemesCount > 0) {
        setTrends(generateTrendsData(actualSchemesCount));
      }
    }, 60000); // Check every minute for day change
    
    return () => clearInterval(checkDayChange);
  }, [actualSchemesCount, trends]);

  const fetchDashboardData = async () => {
    try {
      const [kpisRes, alertsRes, schemesRes] = await Promise.all([
        api.dashboard.getKPIs(),
        api.dashboard.getCriticalFeed(5),
        api.schemes.getAll()
      ]);
      
      setKpis(kpisRes.data);
      
      // Get actual schemes count from API
      const schemesCount = schemesRes.data.length;
      setActualSchemesCount(schemesCount);
      
      // Generate fresh trends data with real dates and actual schemes count
      setTrends(generateTrendsData(schemesCount));
      
      setCriticalAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <Zap className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">INITIALIZING COMMAND CENTER...</p>
          <p className="text-gray-500 text-sm mt-2">Loading real-time data</p>
        </div>
      </div>
    );
  }

  const coveragePercentage = kpis?.coverage_percentage || 0;
  const nrwPercentage = kpis?.nrw_percentage || 0;
  const activeSchemePercentage = kpis?.active_schemes && kpis?.total_schemes 
    ? ((kpis.active_schemes / kpis.total_schemes) * 100).toFixed(1)
    : 0;

  const COLORS = ['#00d9ff', '#00e5cc', '#00ff88', '#ffaa00'];

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      <Breadcrumb items={[{ label: 'Executive Overview' }]} onNavigate={setActiveModule} />
      
      {/* Hero Section with Animated Background */}
      <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-[#12172e] via-[#0f1629] to-[#12172e] p-6 md:p-8 shadow-2xl">
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(0deg, rgba(0, 217, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 217, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            animation: 'gridScroll 20s linear infinite'
          }}></div>
        </div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-2 h-2 bg-cyan-400 rounded-full top-1/4 left-1/4 animate-ping" style={{ animationDuration: '3s', animationDelay: '0s' }}></div>
          <div className="absolute w-2 h-2 bg-teal-400 rounded-full top-1/2 right-1/4 animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
          <div className="absolute w-2 h-2 bg-green-400 rounded-full bottom-1/4 left-1/3 animate-ping" style={{ animationDuration: '3s', animationDelay: '2s' }}></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-wide">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                  EXECUTIVE OVERVIEW
                </span>
              </h1>
              <p className="text-gray-400 text-sm md:text-base">Real-time Water Resource Management System</p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-2">
                <Shield className="text-cyan-400" size={18} />
                <span className="text-cyan-400 font-semibold text-sm">SECURE</span>
              </div>
              <div className="px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                <Wifi className="text-green-400" size={18} />
                <span className="text-green-400 font-semibold text-sm">LIVE</span>
              </div>
            </div>
          </div>
          
          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Water Resource Coverage */}
            <div 
              className="group relative p-5 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 hover:border-cyan-500/60 transition-all cursor-pointer hover:shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-1"
              onClick={() => setActiveModule('network')}
              title="Click to view schemes"
            >
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400 rounded-tl-xl"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400 rounded-br-xl"></div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-cyan-500/20 border border-cyan-500/40 glow-cyan">
                  <Droplets className="text-cyan-400" size={20} />
                </div>
                <span className="text-sm text-gray-300 font-medium tracking-wide">WATER COVERAGE</span>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-4xl font-bold text-cyan-400 text-glow-cyan">{coveragePercentage}%</span>
                  <span className="text-xs text-gray-400">TARGET: 90%</span>
                </div>
                
                <div className="relative h-3 bg-slate-800/50 rounded-full overflow-hidden border border-cyan-500/20">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 via-cyan-400 to-teal-400 rounded-full transition-all duration-1000 relative"
                    style={{ width: `${coveragePercentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">
                    <span className="text-cyan-400 font-semibold">{kpis?.households_served?.toLocaleString()}</span> / {kpis?.households_total?.toLocaleString()}
                  </span>
                  <span className="text-gray-500">households</span>
                </div>
              </div>
            </div>

            {/* NRW Status */}
            <div 
              className="group relative p-5 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 hover:border-orange-500/60 transition-all cursor-pointer hover:shadow-lg hover:shadow-orange-500/20 hover:-translate-y-1"
              onClick={() => setActiveModule('nrw')}
              title="Click to view NRW details"
            >
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-orange-400 rounded-tl-xl"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-orange-400 rounded-br-xl"></div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-orange-500/20 border border-orange-500/40 glow-amber">
                  <AlertTriangle className="text-orange-400" size={20} />
                </div>
                <span className="text-sm text-gray-300 font-medium tracking-wide">NON-REVENUE WATER</span>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className={`text-4xl font-bold ${nrwPercentage > 20 ? 'text-red-400' : 'text-orange-400'}`}>
                    {nrwPercentage}%
                  </span>
                  <span className="text-xs text-gray-400">TARGET: &lt;15%</span>
                </div>
                
                <div className="relative h-3 bg-slate-800/50 rounded-full overflow-hidden border border-orange-500/20">
                  <div 
                    className={`h-full rounded-full transition-all duration-1000 relative ${
                      nrwPercentage > 20 
                        ? 'bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500' 
                        : 'bg-gradient-to-r from-green-500 via-teal-500 to-emerald-400'
                    }`}
                    style={{ width: `${nrwPercentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-semibold ${nrwPercentage > 20 ? 'text-red-400' : 'text-green-400'}`}>
                    {nrwPercentage > 20 ? 'ABOVE TARGET' : 'WITHIN TARGET'}
                  </span>
                  <span className="text-gray-500">Current: {nrwPercentage}%</span>
                </div>
              </div>
            </div>

            {/* Active Schemes */}
            <div 
              className="group relative p-5 rounded-xl bg-gradient-to-br from-green-500/10 to-teal-500/10 border border-green-500/30 hover:border-green-500/60 transition-all cursor-pointer hover:shadow-lg hover:shadow-green-500/20 hover:-translate-y-1"
              onClick={() => setActiveModule('network')}
              title="Click to view all schemes"
            >
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-green-400 rounded-tl-xl"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-green-400 rounded-br-xl"></div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-green-500/20 border border-green-500/40 glow-green">
                  <Activity className="text-green-400" size={20} />
                </div>
                <span className="text-sm text-gray-300 font-medium tracking-wide">ACTIVE SCHEMES</span>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-4xl font-bold text-green-400 text-glow-white">{activeSchemePercentage}%</span>
                  <span className="text-xs text-gray-400">OPERATIONAL</span>
                </div>
                
                <div className="relative h-3 bg-slate-800/50 rounded-full overflow-hidden border border-green-500/20">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 via-teal-500 to-emerald-400 rounded-full transition-all duration-1000 relative"
                    style={{ width: `${activeSchemePercentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">
                    <span className="text-green-400 font-semibold">{kpis?.active_schemes}</span> / {kpis?.total_schemes}
                  </span>
                  <span className="text-gray-500">schemes active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Featured Modules Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="h-0.5 flex-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          <h2 className="text-base sm:text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400 tracking-wide whitespace-nowrap">
            ⚡ FEATURED MODULES
          </h2>
          <div className="h-0.5 flex-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* ILK Hunter Module */}
          <div 
            onClick={() => setActiveModule('ilkhunter')}
            className="group relative bg-gradient-to-br from-red-900/20 via-[#12172e] to-orange-900/20 border-2 border-red-500/30 active:border-red-500/60 sm:hover:border-red-500/60 rounded-xl p-4 sm:p-6 cursor-pointer transition-all duration-300 active:shadow-2xl active:shadow-red-500/30 sm:hover:shadow-2xl sm:hover:shadow-red-500/30 active:-translate-y-1 sm:hover:-translate-y-2 overflow-hidden"
          >
            {/* Animated background pulse */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-orange-500/0 group-hover:from-red-500/10 group-hover:to-orange-500/10 transition-all duration-500"></div>
            
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 border-t-2 border-l-2 border-red-500 rounded-tl-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 border-b-2 border-r-2 border-orange-500 rounded-br-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/40 group-hover:scale-110 transition-transform">
                  <AlertTriangle className="text-red-400" size={24} />
                </div>
                <div className="px-2 sm:px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40">
                  <span className="text-xs font-bold text-red-400">NEW</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                ILK Hunter
              </h3>
              <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4">
                Illegal Connection & Leakage Detection System
              </p>

              {/* Features List */}
              <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></div>
                  <span>Real-time theft detection</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0"></div>
                  <span>Automated leak tracking</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0"></div>
                  <span>Priority-based alerts</span>
                </li>
              </ul>

              {/* Action Button */}
              <button className="w-full py-2.5 px-4 bg-gradient-to-r from-red-600 to-orange-600 active:from-red-500 active:to-orange-500 sm:hover:from-red-500 sm:hover:to-orange-500 text-white rounded-lg font-semibold text-sm transition-all group-hover:shadow-lg group-hover:shadow-red-500/50 flex items-center justify-center gap-2">
                Track
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>

          {/* Valve Control Module */}
          <div 
            onClick={() => setActiveModule('valve-control')}
            className="group relative bg-gradient-to-br from-blue-900/20 via-[#12172e] to-cyan-900/20 border-2 border-cyan-500/30 active:border-cyan-500/60 sm:hover:border-cyan-500/60 rounded-xl p-4 sm:p-6 cursor-pointer transition-all duration-300 active:shadow-2xl active:shadow-cyan-500/30 sm:hover:shadow-2xl sm:hover:shadow-cyan-500/30 active:-translate-y-1 sm:hover:-translate-y-2 overflow-hidden"
          >
            {/* Animated background pulse */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-500"></div>
            
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 border-t-2 border-l-2 border-cyan-500 rounded-tl-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 border-b-2 border-r-2 border-blue-500 rounded-br-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 group-hover:scale-110 transition-transform">
                  <Activity className="text-cyan-400" size={24} />
                </div>
                <div className="px-2 sm:px-3 py-1 rounded-full bg-green-500/20 border border-green-500/40">
                  <span className="text-xs font-bold text-green-400">LIVE</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                Valve Control
              </h3>
              <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4">
                AR/VR Enhanced Real-time Valve Operations
              </p>

              {/* Features List */}
              <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0"></div>
                  <span>Remote valve operations</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></div>
                  <span>Health monitoring</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0"></div>
                  <span>Batch operations</span>
                </li>
              </ul>

              {/* Action Button */}
              <button className="w-full py-2.5 px-4 bg-gradient-to-r from-cyan-600 to-blue-600 active:from-cyan-500 active:to-blue-500 sm:hover:from-cyan-500 sm:hover:to-blue-500 text-white rounded-lg font-semibold text-sm transition-all group-hover:shadow-lg group-hover:shadow-cyan-500/50 flex items-center justify-center gap-2">
                Track
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>

          {/* Billing & Revenue Module */}
          <div 
            onClick={() => setActiveModule('billing')}
            className="group relative bg-gradient-to-br from-green-900/20 via-[#12172e] to-emerald-900/20 border-2 border-green-500/30 active:border-green-500/60 sm:hover:border-green-500/60 rounded-xl p-4 sm:p-6 cursor-pointer transition-all duration-300 active:shadow-2xl active:shadow-green-500/30 sm:hover:shadow-2xl sm:hover:shadow-green-500/30 active:-translate-y-1 sm:hover:-translate-y-2 overflow-hidden"
          >
            {/* Animated background pulse */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-emerald-500/0 group-hover:from-green-500/10 group-hover:to-emerald-500/10 transition-all duration-500"></div>
            
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 border-t-2 border-l-2 border-green-500 rounded-tl-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 border-b-2 border-r-2 border-emerald-500 rounded-br-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/40 group-hover:scale-110 transition-transform">
                  <span className="text-2xl sm:text-3xl">₹</span>
                </div>
                <div className="px-2 sm:px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/40">
                  <span className="text-xs font-bold text-yellow-400">HOT</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors">
                Billing & Revenue
              </h3>
              <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4">
                Revenue Assurance & Collection Analytics
              </p>

              {/* Features List */}
              <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"></div>
                  <span>Consumer management</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></div>
                  <span>Payment tracking</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0"></div>
                  <span>Revenue leak detection</span>
                </li>
              </ul>

              {/* Action Button */}
              <button className="w-full py-2.5 px-4 bg-gradient-to-r from-green-600 to-emerald-600 active:from-green-500 active:to-emerald-500 sm:hover:from-green-500 sm:hover:to-emerald-500 text-white rounded-lg font-semibold text-sm transition-all group-hover:shadow-lg group-hover:shadow-green-500/50 flex items-center justify-center gap-2">
                Track
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Row - Map, Network, Alerts with Tech Style */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 3D Map Card */}
        <Card className="bg-[#12172e] border-cyan-500/30 text-white cursor-pointer hover:border-cyan-500/60 transition-all hover:shadow-lg hover:shadow-cyan-500/20 group relative overflow-hidden" onClick={() => setActiveModule('map')}>
          {/* Scan line effect */}
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
                  <Zap size={14} className="text-cyan-400" />
                </div>
                <CardTitle className="text-lg font-semibold tracking-wide">INTERACTIVE MAP</CardTitle>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">3D Globe View • Real-time</p>
          </CardHeader>
          <CardContent>
            <div className="relative bg-gradient-to-br from-blue-900/30 to-slate-900 rounded-lg h-48 flex items-center justify-center border border-cyan-500/20 overflow-hidden group-hover:border-cyan-500/40 transition-colors">
              {/* 3D Globe Visualization */}
              <div className="relative w-40 h-40">
                {/* Globe sphere */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-600/30 via-blue-500/20 to-teal-800/30 border-2 border-cyan-500/40 shadow-2xl">
                  {/* Latitude lines */}
                  <div className="absolute top-1/4 left-0 right-0 h-0.5 bg-cyan-400/30"></div>
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-cyan-400/40"></div>
                  <div className="absolute top-3/4 left-0 right-0 h-0.5 bg-cyan-400/30"></div>
                  
                  {/* Longitude lines */}
                  <div className="absolute top-0 bottom-0 left-1/4 w-0.5 bg-cyan-400/30 rounded-full"></div>
                  <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-cyan-400/40 rounded-full"></div>
                  <div className="absolute top-0 bottom-0 left-3/4 w-0.5 bg-cyan-400/30 rounded-full"></div>
                  
                  {/* Location markers with glow */}
                  <div className="absolute top-1/3 left-1/2 w-3 h-3 -ml-1.5 -mt-1.5">
                    <div className="w-full h-full bg-red-500 rounded-full animate-ping opacity-75 shadow-lg shadow-red-500/50"></div>
                    <div className="absolute inset-0 w-full h-full bg-red-500 rounded-full shadow-lg shadow-red-500/50"></div>
                  </div>
                  <div className="absolute top-2/3 left-1/3 w-2 h-2 -ml-1 -mt-1">
                    <div className="w-full h-full bg-green-500 rounded-full shadow-lg shadow-green-500/50"></div>
                  </div>
                  <div className="absolute top-1/2 left-2/3 w-2 h-2 -ml-1 -mt-1">
                    <div className="w-full h-full bg-yellow-500 rounded-full shadow-lg shadow-yellow-500/50"></div>
                  </div>
                  
                  {/* Enhanced glow effect */}
                  <div className="absolute inset-0 rounded-full bg-cyan-500/10 blur-xl"></div>
                </div>
                
                {/* Orbiting element */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
                  <div className="absolute top-0 left-1/2 w-1.5 h-1.5 -ml-0.75 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/80"></div>
                </div>
              </div>
              
              <div className="absolute bottom-3 text-center w-full">
                <p className="text-xs text-cyan-400 font-semibold tracking-wide">SOUTH GOA NETWORK</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Hierarchy Card */}
        <Card className="bg-[#12172e] border-teal-500/30 text-white cursor-pointer hover:border-teal-500/60 transition-all hover:shadow-lg hover:shadow-teal-500/20 group relative overflow-hidden" onClick={() => setActiveModule('network')}>
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-teal-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-teal-500/20 border border-teal-500/40">
                  <Activity size={14} className="text-teal-400" />
                </div>
                <CardTitle className="text-lg font-semibold tracking-wide">NETWORK</CardTitle>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Infrastructure Hierarchy</p>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-center justify-center bg-slate-900/50 rounded-lg border border-teal-500/20 group-hover:border-teal-500/40 transition-colors relative overflow-hidden">
              {/* Grid background */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'linear-gradient(0deg, rgba(0, 229, 204, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 229, 204, 0.2) 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }}></div>
              
              {/* Hierarchical Network Diagram */}
              <div className="relative w-full h-full p-4">
                {/* Top Level - District */}
                <div className="absolute top-4 left-1/2 -ml-8">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-600/40 to-cyan-800/40 border-2 border-cyan-400 flex flex-col items-center justify-center shadow-lg glow-cyan">
                    <Droplets size={20} className="text-cyan-400" />
                    <span className="text-xs text-cyan-400 mt-1 font-semibold">DISTRICT</span>
                  </div>
                </div>
                
                {/* Connecting lines with glow */}
                <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                  <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#00d9ff" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#00e5cc" stopOpacity="0.8"/>
                    </linearGradient>
                  </defs>
                  <line x1="50%" y1="60" x2="25%" y2="110" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="4" />
                  <line x1="50%" y1="60" x2="75%" y2="110" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="4" />
                </svg>
                
                {/* Second Level - Talukas */}
                <div className="absolute top-24 left-1/4 -ml-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-600/40 to-teal-800/40 border-2 border-teal-400 flex flex-col items-center justify-center shadow-md glow-teal">
                    <span className="text-xs text-teal-400 font-bold">T1</span>
                  </div>
                </div>
                <div className="absolute top-24 right-1/4 -mr-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-600/40 to-teal-800/40 border-2 border-teal-400 flex flex-col items-center justify-center shadow-md glow-teal">
                    <span className="text-xs text-teal-400 font-bold">T2</span>
                  </div>
                </div>
                
                {/* Third Level - Villages/Assets */}
                <div className="absolute bottom-6 left-8">
                  <div className="w-8 h-8 rounded-md bg-green-500/40 border-2 border-green-400 flex items-center justify-center glow-green">
                    <span className="text-xs text-green-400 font-bold">V</span>
                  </div>
                </div>
                <div className="absolute bottom-6 left-1/3 -ml-4">
                  <div className="w-8 h-8 rounded-md bg-green-500/40 border-2 border-green-400 flex items-center justify-center glow-green">
                    <span className="text-xs text-green-400 font-bold">V</span>
                  </div>
                </div>
                <div className="absolute bottom-6 right-1/3 -mr-4">
                  <div className="w-8 h-8 rounded-md bg-green-500/40 border-2 border-green-400 flex items-center justify-center glow-green">
                    <span className="text-xs text-green-400 font-bold">V</span>
                  </div>
                </div>
                <div className="absolute bottom-6 right-8">
                  <div className="w-8 h-8 rounded-md bg-green-500/40 border-2 border-green-400 flex items-center justify-center glow-green">
                    <span className="text-xs text-green-400 font-bold">V</span>
                  </div>
                </div>
                
                {/* Labels */}
                <div className="absolute bottom-1 left-1/2 -ml-20 text-center">
                  <span className="text-xs text-teal-400 font-semibold tracking-wide">DISTRICT → TALUKA → VILLAGE</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts Card */}
        <Card className="bg-[#12172e] border-orange-500/30 text-white cursor-pointer hover:border-orange-500/60 transition-all hover:shadow-lg hover:shadow-orange-500/20 group relative overflow-hidden" onClick={() => setActiveModule('alerts')}>
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-orange-500/20 border border-orange-500/40">
                  <AlertTriangle size={14} className="text-orange-400" />
                </div>
                <CardTitle className="text-lg font-semibold tracking-wide">ALERTS</CardTitle>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Real-time Monitoring</p>
          </CardHeader>
          <CardContent>
            <div className="flex justify-around items-center h-48 relative">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5 rounded-lg"></div>
              
              {/* Critical Alert */}
              <div className="flex flex-col items-center relative z-10">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full border-4 border-red-500/30 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center glow-red">
                      <AlertTriangle className="text-red-500" size={32} />
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold border-2 border-[#12172e] shadow-lg shadow-red-500/50">
                    {kpis?.critical_incidents || 0}
                  </div>
                </div>
                <p className="text-xs text-red-400 mt-2 text-center font-semibold tracking-wide">CRITICAL</p>
              </div>

              {/* Major Alert */}
              <div className="flex flex-col items-center relative z-10">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full border-4 border-orange-500/30 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center glow-amber">
                      <AlertTriangle className="text-orange-500" size={32} />
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-xs font-bold border-2 border-[#12172e] shadow-lg shadow-orange-500/50">
                    {kpis?.major_incidents || 0}
                  </div>
                </div>
                <p className="text-xs text-orange-400 mt-2 text-center font-semibold tracking-wide">MAJOR</p>
              </div>

              {/* Resolved */}
              <div className="flex flex-col items-center relative z-10">
                <div className="w-20 h-20 rounded-full border-4 border-green-500/30 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center glow-green">
                    <CheckCircle className="text-green-500" size={32} />
                  </div>
                </div>
                <p className="text-xs text-green-400 mt-2 text-center font-semibold tracking-wide">RESOLVED</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row - Reports & Action Buttons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reports */}
        <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
                <FileText size={14} className="text-cyan-400" />
              </div>
              <CardTitle className="text-lg font-semibold tracking-wide">REPORTS</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex justify-around items-center">
              {['Daily Brief', 'Weekly Summary', 'Monthly Report'].map((report, idx) => (
                <div 
                  key={idx} 
                  className="flex flex-col items-center cursor-pointer group"
                  onClick={() => setActiveModule('reports')}
                  title="Click to view Reports"
                >
                  <div className="w-16 h-20 bg-gradient-to-br from-slate-800/80 to-slate-900 rounded-lg border-2 border-cyan-500/30 flex flex-col items-center justify-center mb-2 group-hover:border-cyan-500/60 transition-all group-hover:shadow-lg group-hover:shadow-cyan-500/20 group-hover:-translate-y-1">
                    <div className="w-10 h-2 bg-cyan-500 rounded mb-1 group-hover:shadow-lg group-hover:shadow-cyan-500/50"></div>
                    <div className="w-10 h-1 bg-gray-600 rounded mb-1"></div>
                    <div className="w-10 h-1 bg-gray-600 rounded mb-1"></div>
                    <div className="w-10 h-1 bg-gray-600 rounded"></div>
                  </div>
                  <p className="text-xs text-gray-400 group-hover:text-cyan-400 transition-colors font-medium">{report}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
                <Zap size={14} className="text-cyan-400" />
              </div>
              <CardTitle className="text-lg font-semibold tracking-wide">QUICK ACTIONS</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <button 
                className="relative bg-gradient-to-br from-blue-600/80 to-blue-700/80 border border-blue-500/50 rounded-lg px-6 py-4 text-sm font-medium transition-all flex flex-col items-center justify-center gap-2 group hover:from-blue-600 hover:to-blue-700 hover:shadow-lg hover:shadow-blue-500/30 overflow-hidden"
                onClick={() => setActiveModule('communication')}
                title="Click to view Communication module"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <Bell size={22} className="text-white relative z-10" />
                <span className="text-white relative z-10 font-semibold tracking-wide">In App</span>
              </button>
              <button 
                className="relative bg-gradient-to-br from-green-600/80 to-green-700/80 border border-green-500/50 rounded-lg px-6 py-4 text-sm font-medium transition-all flex flex-col items-center justify-center gap-2 group hover:from-green-600 hover:to-green-700 hover:shadow-lg hover:shadow-green-500/30 overflow-hidden"
                onClick={() => setActiveModule('communication')}
                title="Click to view Communication module"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <MessageSquare size={22} className="text-white relative z-10" />
                <span className="text-white relative z-10 font-semibold tracking-wide">WhatsApp</span>
              </button>
              <button 
                className="relative bg-gradient-to-br from-purple-600/80 to-purple-700/80 border border-purple-500/50 rounded-lg px-6 py-4 text-sm font-medium transition-all flex flex-col items-center justify-center gap-2 group hover:from-purple-600 hover:to-purple-700 hover:shadow-lg hover:shadow-purple-500/30 overflow-hidden"
                onClick={() => setActiveModule('communication')}
                title="Click to view Communication module"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <Mail size={22} className="text-white relative z-10" />
                <span className="text-white relative z-10 font-semibold tracking-wide">Email</span>
              </button>
              <button 
                className="relative bg-gradient-to-br from-orange-600/80 to-orange-700/80 border border-orange-500/50 rounded-lg px-6 py-4 text-sm font-medium transition-all flex flex-col items-center justify-center gap-2 group hover:from-orange-600 hover:to-orange-700 hover:shadow-lg hover:shadow-orange-500/30 overflow-hidden"
                onClick={() => setActiveModule('communication')}
                title="Click to view Communication module"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <Phone size={22} className="text-white relative z-10" />
                <span className="text-white relative z-10 font-semibold tracking-wide">Call</span>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trends Chart */}
        <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
                <TrendingUp size={14} className="text-cyan-400" />
              </div>
              <CardTitle className="text-lg font-semibold tracking-wide">7-DAY ACTIVITY TRENDS</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends}>
                <defs>
                  <linearGradient id="colorSchemes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00d9ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#00d9ff" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff0055" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#ff0055" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ff88" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#00ff88" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  stroke="#94a3b8" 
                  style={{ fontSize: '12px', fontWeight: '500' }}
                />
                <YAxis 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px', fontWeight: '500' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#12172e', 
                    border: '1px solid rgba(0, 217, 255, 0.3)', 
                    borderRadius: '8px',
                    boxShadow: '0 0 20px rgba(0, 217, 255, 0.2)'
                  }}
                  labelStyle={{ color: '#00d9ff', fontWeight: '600' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Legend 
                  wrapperStyle={{ 
                    paddingTop: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                />
                <Line 
                  type="linear" 
                  dataKey="schemes" 
                  stroke="#00d9ff" 
                  strokeWidth={3} 
                  name="Schemes" 
                  dot={{ fill: '#00d9ff', r: 5 }}
                  activeDot={{ r: 7, fill: '#00d9ff', stroke: '#fff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
                <Line 
                  type="linear" 
                  dataKey="incidents" 
                  stroke="#ff0055" 
                  strokeWidth={3} 
                  name="Incidents" 
                  dot={{ fill: '#ff0055', r: 5 }}
                  activeDot={{ r: 7, fill: '#ff0055', stroke: '#fff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
                <Line 
                  type="linear" 
                  dataKey="resolved" 
                  stroke="#00ff88" 
                  strokeWidth={3} 
                  name="Resolved" 
                  strokeDasharray="5 5" 
                  dot={{ fill: '#00ff88', r: 5 }}
                  activeDot={{ r: 7, fill: '#00ff88', stroke: '#fff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Critical Alerts Feed */}
        <Card className="bg-[#12172e] border-orange-500/30 text-white hover:border-orange-500/50 transition-all">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-orange-500/20 border border-orange-500/40">
                <AlertTriangle size={14} className="text-orange-400" />
              </div>
              <CardTitle className="text-lg font-semibold tracking-wide">CRITICAL ALERTS FEED</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
              {criticalAlerts && criticalAlerts.length > 0 ? (
                criticalAlerts.map((alert, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 hover:border-cyan-500/50 transition-all cursor-pointer group relative overflow-hidden"
                    onClick={() => setActiveModule('alerts')}
                  >
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-cyan-500/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    <div className="flex items-start gap-3 relative z-10">
                      <div className={`w-2 h-2 mt-1.5 rounded-full animate-pulse ${
                        alert.severity === 'Critical' ? 'bg-red-500 shadow-lg shadow-red-500/50' : 
                        alert.severity === 'Major' ? 'bg-orange-500 shadow-lg shadow-orange-500/50' : 
                        'bg-yellow-500 shadow-lg shadow-yellow-500/50'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium group-hover:text-cyan-400 transition-colors">{alert.description}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                          <span className={`px-2 py-0.5 rounded-full ${
                            alert.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                            alert.severity === 'Major' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {alert.type}
                          </span>
                          <span>•</span>
                          <span>{new Date(alert.raised_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-teal-500/5 rounded-lg"></div>
                  <div className="relative z-10">
                    <CheckCircle size={48} className="mx-auto mb-3 text-green-500 animate-pulse" />
                    <p className="text-base font-semibold text-green-400">ALL SYSTEMS OPERATIONAL</p>
                    <p className="text-sm text-gray-500 mt-1">No critical alerts detected</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
